import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { BROWN } from '../assets';

gsap.registerPlugin(ScrollTrigger);

/* ── Spark particle generator ─────────────────────────────── */
function Sparks() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let particles = [];
    let raf;
    let triggered = false;

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener('resize', resize);

    const spawn = () => {
      if (triggered) return;
      triggered = true;
      const cx = canvas.offsetWidth / 2;
      for (let j = 0; j < 40; j++) {
        particles.push({
          x: cx,
          y: Math.random() * canvas.offsetHeight,
          vx: (Math.random() - 0.5) * 8,
          vy: (Math.random() - 0.5) * 4,
          r: Math.random() * 2.5 + 0.5,
          life: 1,
          decay: 0.008 + Math.random() * 0.012,
        });
      }
    };

    const st = ScrollTrigger.create({
      trigger: canvas.closest('section'),
      start: 'top 30%',
      onEnter: spawn,
    });

    const draw = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      particles = particles.filter((p) => p.life > 0);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.06;
        p.life -= p.decay;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212,175,55,${p.life})`;
        ctx.shadowColor = 'rgba(212,175,55,0.8)';
        ctx.shadowBlur = 6;
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      st.kill();
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full z-20 pointer-events-none"
    />
  );
}

export default function Ch4Cutting() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=160%',
          scrub: 1,
          pin: true,
        },
      });
      tl.fromTo('.cut-line', { scaleY: 0 }, { scaleY: 1, duration: 1, ease: 'none' })
        .to('.cut-flash', { opacity: 0.5, duration: 0.08 })
        .to('.cut-flash', { opacity: 0, duration: 0.15 })
        .to('.half-left', { xPercent: -16, rotate: -1.5, duration: 1 }, 'split')
        .to('.half-right', { xPercent: 16, rotate: 1.5, duration: 1 }, 'split')
        .fromTo('.cut-caption', { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 0.6 }, 'split+=0.3');
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section id="ch-4" ref={sectionRef} data-testid="chapter-4" className="relative h-screen overflow-hidden bg-[#0A0A0A] flex items-center justify-center">
      <div className="absolute top-24 left-6 md:left-16 z-20">
        <div className="flex items-center gap-4">
          <span className="font-accent text-[10px] md:text-xs text-[#8C5A35]">CH. 04</span>
          <span className="h-px w-12 md:w-20 bg-[#B89B72]/40" />
          <span className="font-accent text-[10px] md:text-xs text-[#A3A3A3]">Precision Cutting</span>
        </div>
      </div>

      <div className="relative w-[86vw] max-w-4xl aspect-[16/10]">
        <div className="half-left absolute inset-y-0 left-0 w-1/2 overflow-hidden will-change-transform">
          <img src={BROWN.torsoWide} alt="Brown suede panel being cut" className="absolute inset-y-0 left-0 h-full w-[200vw] max-w-none object-cover" style={{ width: '200%' }} />
        </div>
        <div className="half-right absolute inset-y-0 right-0 w-1/2 overflow-hidden will-change-transform">
          <img src={BROWN.torsoWide} alt="Brown suede panel being cut" className="absolute inset-y-0 right-0 h-full max-w-none object-cover" style={{ width: '200%' }} />
        </div>
        <div className="cut-line absolute left-1/2 top-0 bottom-0 w-px bg-[#D4AF37] origin-top shadow-[0_0_18px_rgba(212,175,55,0.8)]" />
        <div className="cut-flash absolute inset-0 bg-[#D4AF37] opacity-0 pointer-events-none" />
        <Sparks />
      </div>

      <div className="cut-caption absolute bottom-28 md:bottom-20 left-6 right-6 md:right-auto md:left-16 max-w-md opacity-0">
        <h2 className="font-display font-light text-3xl md:text-5xl leading-tight tracking-tight">
          One blade.<br /><span className="italic text-[#B89B72]">Zero hesitation.</span>
        </h2>
        <p className="font-body font-light text-sm text-[#A3A3A3] mt-4 leading-relaxed">
          Each panel is cut by hand along the blueprint line — the grain aligned,
          the edge clean enough to stitch without correction.
        </p>
      </div>
    </section>
  );
}
